import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";
import themedLazy from "../../utils/themedLazy";
import find from "lodash/find";
import { useQuery } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";

import TopBanner from "../TopBanner";
import PromoBar from "../PromoBar/PromoBar";
import HoverPopover from "../ui/HoverPopover";
import { setBanners } from "../../redux/reducer/imageReducer";
import { getAllCategory } from "../../services/categoryServices";
import { getBanner } from "../../services/flieServices";
import * as userServices from "../../services/userServices";
import AccountMenu from "./AccountMenu";
import BottomNav from "./BottomNav";
import Cart from "./Cart";
import MobileDrawer from "./MobileDrawer";
import SearchBox from "./SearchBox";

// Popup đăng nhập/đăng ký/quên mật khẩu (antd Form/Input/Modal) chỉ tải khi mở → không nằm trong JS chính
const Login = themedLazy(() => import("../Login"));
const Register = themedLazy(() => import("../Register"));
const ForgotPassword = themedLazy(() => import("../ForgotPassword"));
// Menu danh mục lớn (desktop, chỉ hiện khi rê chuột) — kèm react-number-format, không cần ở lần tải đầu
const MenuDropdown = lazy(() => import("./MenuDropdown"));

const Logo = ({ className = "" }) => (
  <Link to="/" className={`flex flex-shrink-0 items-center ${className}`} aria-label="HealthyMall — Trang chủ">
    <img src="/logo.webp" alt="HealthyMall" width={168} height={27} className="h-auto w-[132px] lg:w-[168px]" />
  </Link>
);

const Header = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const { cart } = useSelector((state) => state?.cartReducer);
  const { banners } = useSelector((state) => state?.imageReducer);
  const { isShowRegisterModal, isShowLoginModal, isShowForgotModal } = useSelector((state) => state?.modalReducer);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [showTop, setShowTop] = useState(false);

  const { data: bannerList, isSuccess: bannerSuccess } = useQuery({ queryKey: "getBanners", queryFn: getBanner, refetchOnWindowFocus: false });
  useEffect(() => {
    if (bannerSuccess && bannerList) dispatch(setBanners(bannerList));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bannerSuccess, bannerList]);

  const { data: categories } = useQuery({ queryKey: "getAllCategory", queryFn: getAllCategory, refetchOnWindowFocus: false });

  const cartCount = useMemo(() => (cart ?? []).reduce((sum, item) => sum + (Number(item?.quantity) || 0), 0), [cart]);

  // Đóng menu mobile khi đổi trang
  useEffect(() => {
    setDrawerOpen(false);
    setCartOpen(false);
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      const res = await userServices.logoutUser(null, dispatch);
      // Tải riêng notification khi cần (giữ antd ngoài JS chính)
      import("antd/es/notification").then(({ default: notification }) =>
        notification.success({ description: res?.response?.data?.message || "Đăng xuất thành công!", placement: "top" })
      );
    } finally {
      navigate("/");
    }
  }, [dispatch, navigate]);

  const topBanner = find(banners, (item) => item?.name === "banner_top");
  // Thanh khuyến mãi đầu trang (Dashboard → Banner & hình ảnh → Thanh khuyến mãi):
  // auto = tự đổi theo đợt sale ngày đôi (mặc định) | image = ảnh banner_top tự thiết kế | off = ẩn
  const promoConfig = find(banners, (item) => item?.name === "promo_bar");
  const promoMode = promoConfig?.mode || "auto";

  return (
    <>
      {promoMode === "auto" && <PromoBar config={promoConfig} />}
      {promoMode === "image" && topBanner?.url && (
        <div className="hidden lg:block">
          <TopBanner src={topBanner.url} color={topBanner.color || "white"} />
        </div>
      )}

      <header className="sticky top-0 z-[700] bg-brand-600 shadow-[0_1px_0_rgba(0,0,0,0.04)]">
        {/* Hàng chính */}
        <div className="container-hm flex items-center gap-3 py-2.5 lg:gap-6 lg:py-3">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="-ml-1 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-white hover:bg-white/10 lg:hidden"
            aria-label="Mở menu"
          >
            <i className="bi bi-list text-2xl" aria-hidden="true" />
          </button>

          <Logo className="mr-auto lg:mr-0" />

          <div className="hidden max-w-[640px] flex-1 lg:block">
            <SearchBox variant="desktop" />
          </div>

          <div className="flex items-center gap-1 lg:ml-auto lg:gap-2">
            <a
              href="tel:+84967711633"
              className="hidden items-center gap-2 rounded-xl px-2 py-1.5 text-white no-underline transition-colors hover:bg-white/10 xl:flex"
            >
              <i className="bi bi-headset text-2xl leading-none" aria-hidden="true" />
              <span className="flex flex-col leading-tight">
                <span className="text-sm text-white/80">Tư vấn miễn phí</span>
                <span className="text-base font-semibold">0967.711.633</span>
              </span>
            </a>

            <div className="hidden lg:block">
              <AccountMenu onLogout={handleLogout} />
            </div>

            <HoverPopover open={cartOpen} onOpenChange={setCartOpen} content={<Cart onClose={() => setCartOpen(false)} />}>
              <Link
                to="/gio-hang"
                className="relative flex items-center gap-2 rounded-xl px-2 py-1.5 text-white no-underline transition-colors hover:bg-white/10"
                aria-label={`Giỏ hàng, ${cartCount} sản phẩm`}
              >
                <span className="relative">
                  <i className="bi bi-bag text-2xl leading-none" aria-hidden="true" />
                  {cartCount > 0 && (
                    <span className="absolute -right-2 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-amber-400 px-1 text-xs font-bold leading-none text-slate-900 ring-2 ring-brand-600">
                      {cartCount > 99 ? "99+" : cartCount}
                    </span>
                  )}
                </span>
                <span className="hidden text-base font-semibold lg:inline">Giỏ hàng</span>
              </Link>
            </HoverPopover>
          </div>
        </div>

        {/* Tìm kiếm (mobile) */}
        <div className="container-hm pb-2.5 lg:hidden">
          <SearchBox variant="mobile" />
        </div>

        {/* Thanh danh mục (desktop) */}
        <nav aria-label="Danh mục sản phẩm" className="hidden border-b border-slate-200 bg-white lg:block">
          <div className="container-hm flex h-11 items-center gap-1">
            <HoverPopover
              align="left"
              className="mr-2"
              content={
                <Suspense fallback={<div className="h-[300px] w-[880px] max-w-[90vw] animate-pulse rounded-2xl bg-white shadow-pop ring-1 ring-black/5" />}>
                  <MenuDropdown />
                </Suspense>
              }
            >
              <button
                type="button"
                aria-haspopup="true"
                className="flex h-8 items-center gap-2 rounded-lg bg-brand-50 px-3 text-base font-semibold text-brand-700 transition-colors hover:bg-brand-100"
              >
                <i className="bi bi-grid-3x3-gap-fill" aria-hidden="true" />
                Danh mục sản phẩm
                <i className="bi bi-chevron-down text-xs" aria-hidden="true" />
              </button>
            </HoverPopover>

            <ul className="no-scrollbar m-0 flex min-w-0 flex-1 list-none items-center gap-0.5 overflow-x-auto p-0">
              {(categories ?? []).slice(0, 8).map((c, i) => {
                const active = pathname === `/danh-muc/${c?.slug}`;
                // Màn hẹp hiện ít danh mục hơn để không bị cắt chữ; đủ cả ở menu "Danh mục sản phẩm"
                const fit = i >= 6 ? "hidden 2xl:block" : i >= 4 ? "hidden xl:block" : "";
                return (
                  <li key={c?._id || c?.slug} className={`list-none ${fit}`}>
                    <Link
                      to={`/danh-muc/${c?.slug}`}
                      aria-current={active ? "page" : undefined}
                      className={`block whitespace-nowrap rounded-lg px-2.5 py-1.5 text-base font-medium no-underline transition-colors hover:bg-slate-50 hover:text-brand-700 ${
                        active ? "text-brand-700" : "text-slate-700"
                      }`}
                    >
                      {c?.name}
                    </Link>
                  </li>
                );
              })}
            </ul>

            <Link
              to="/flash-sale"
              className="flex flex-shrink-0 items-center gap-1 rounded-lg px-2.5 py-1.5 text-base font-semibold text-sale-600 no-underline hover:bg-sale-50"
            >
              <i className="bi bi-lightning-charge-fill" aria-hidden="true" />
              Flash Sale
            </Link>
            <Link
              to="/bai-viet"
              className="flex flex-shrink-0 items-center gap-1 rounded-lg px-2.5 py-1.5 text-base font-semibold text-slate-700 no-underline hover:bg-slate-50 hover:text-brand-700"
            >
              <i className="bi bi-journal-medical" aria-hidden="true" />
              Góc sức khỏe
            </Link>
          </div>
        </nav>
      </header>

      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} categories={categories} onLogout={handleLogout} />
      <BottomNav cartCount={cartCount} onOpenMenu={() => setDrawerOpen(true)} />

      {/* Nút nổi (desktop): lên đầu trang + chat Zalo */}
      <div className="fixed bottom-6 right-5 z-[600] hidden flex-col gap-2 lg:flex">
        <a
          href="https://zalo.me/0967711633"
          target="_blank"
          rel="noreferrer"
          className="flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-lift ring-1 ring-black/5 transition-transform hover:scale-105"
          aria-label="Chat tư vấn qua Zalo"
        >
          <img src="/zalo.svg" alt="" width={28} height={28} />
        </a>
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className={`flex h-12 w-12 items-center justify-center rounded-full bg-white text-slate-600 shadow-lift ring-1 ring-black/5 transition-all hover:text-brand-700 ${
            showTop ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
          aria-label="Lên đầu trang"
        >
          <i className="bi bi-arrow-up text-lg" aria-hidden="true" />
        </button>
      </div>

      <Suspense fallback={null}>
        {isShowLoginModal && <Login />}
        {isShowRegisterModal && <Register />}
        {isShowForgotModal && <ForgotPassword />}
      </Suspense>
    </>
  );
};

export default Header;
